// src/modals/ClientSatisfactionModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Rating,
  Divider
} from '@mui/material';
import BaseExpandedCard from '../components/cards/BaseExpandedCard';
import { getScoreColor } from '../utils/colors';
import { Logger } from '../utils/logger';

/**
 * ClientSatisfactionModal displays detailed information about client satisfaction
 * metrics for a project, including historical ratings and feedback.
 */
const logger = new Logger('ClientSatisfactionModal');

const ClientSatisfactionModal = ({ open, onClose, projectName, projectId, clientName }) => {
  const [csatData, setCsatData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error] = useState(null);

  // Simulated data for placeholder purposes
  const placeholderData = useMemo(() => ({
    average: 8.5,
    trend: 'increasing',
    ratings: [
      {
        date: new Date('2025-01-15'),
        contact: 'John Smith',
        role: 'Project Sponsor',
        rating: 9,
        feedback: 'Excellent delivery of milestones, very responsive team.'
      },
      {
        date: new Date('2024-12-01'),
        contact: 'Jane Doe',
        role: 'Product Owner',
        rating: 8,
        feedback: 'Good progress but some communication issues with scheduling.'
      }
    ],
    dimensions: {
      'Communication': 9.0,
      'Quality': 8.5,
      'Timeliness': 7.5,
      'Value': 9.0
    },
    last_updated: new Date()
  }), []);

  // Fetch client satisfaction data when the modal opens
  useEffect(() => {
    if (open && projectId) {
      setIsLoading(true);
      
      // In a real implementation, this would be an API call
      // For now, we'll simulate an API call with a timeout
      const timer = setTimeout(() => {
        logger.debug(`Fetching CSAT data for project: ${projectId}`);
        setCsatData(placeholderData);
        setIsLoading(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [open, projectId, placeholderData]);

  // Show loading state
  if (isLoading) {
    return (
      <BaseExpandedCard
        open={open}
        onClose={onClose}
        title={`Client Satisfaction: ${projectName}`}
        subtitle="Loading data..."
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '300px' 
        }}>
          <CircularProgress />
        </Box>
      </BaseExpandedCard>
    );
  }

  // Show error state
  if (error) {
    return (
      <BaseExpandedCard
        open={open}
        onClose={onClose}
        title={`Client Satisfaction: ${projectName}`}
        subtitle="Error loading data"
      >
        <Box sx={{ 
          p: 4, 
          textAlign: 'center', 
          color: 'error.main',
          bgcolor: 'background.paper',
          borderRadius: 1
        }}>
          <Typography variant="h6">Error</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        </Box>
      </BaseExpandedCard>
    );
  }

  // Use the data (either from API or placeholder)
  const data = csatData || placeholderData;

  return (
    <BaseExpandedCard
      open={open}
      onClose={onClose}
      title={`Client Satisfaction: ${projectName}`}
      subtitle={`Ratings and feedback from ${clientName}`}
    >
      <Grid container spacing={3}>
        {/* Overall rating summary */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: getScoreColor(data.average)
                  }}
                >
                  {data.average.toFixed(1)}
                </Typography>
                <Typography variant="subtitle1">
                  Overall Satisfaction (10-point scale)
                </Typography>
                <Rating 
                  value={data.average / 2} 
                  precision={0.5} 
                  readOnly 
                  sx={{ mt: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Satisfaction by dimension */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Satisfaction by Dimension
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                {Object.entries(data.dimensions).map(([dimension, score]) => (
                  <Box key={dimension} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">
                        {dimension}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 'bold',
                          color: getScoreColor(score)
                        }}
                      >
                        {score.toFixed(1)}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      width: '100%', 
                      bgcolor: 'rgba(255,255,255,0.1)',
                      borderRadius: 1,
                      height: 8
                    }}>
                      <Box sx={{ 
                        width: `${(score / 10) * 100}%`,
                        height: '100%',
                        borderRadius: 1,
                        bgcolor: getScoreColor(score)
                      }}/>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Trend and latest ratings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Satisfaction Trend
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip 
                  label={data.trend.charAt(0).toUpperCase() + data.trend.slice(1)} 
                  color={data.trend === 'increasing' ? 'success' : data.trend === 'decreasing' ? 'error' : 'warning'}
                  sx={{ mr: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Compared to previous period
                </Typography>
              </Box>
              
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Recent Feedback
              </Typography>
              
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {data.ratings.map((rating, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      p: 1.5, 
                      mb: 1.5, 
                      borderRadius: 1, 
                      bgcolor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {rating.contact} ({rating.role})
                      </Typography>
                      <Chip 
                        label={rating.rating.toFixed(1)} 
                        size="small"
                        sx={{ 
                          bgcolor: `${getScoreColor(rating.rating)}20`,
                          color: getScoreColor(rating.rating)
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}>
                      "{rating.feedback}"
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {rating.date.toLocaleDateString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Last updated: {data.last_updated ? data.last_updated.toLocaleString() : 'Never'}
        </Typography>
      </Box>
    </BaseExpandedCard>
  );
};

ClientSatisfactionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectName: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  clientName: PropTypes.string
};

export default ClientSatisfactionModal;
